import React, { useMemo } from 'react';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useProjectElementListStore } from '../../shared/store/projectElementStore';
import SelectBox from '../../shared/component/SelectBox';
import { DocumentData } from '../../shared/dto/EntityRepository';
import { SelectType, DisplaySize, DisplayAlignment, ImageStyle, PEInfoType, normalizePEInfoType, DetailType } from '../../shared/enum/EnumRepository';
import { ImgContentCoreWrapper, ImgWrapper, PEHeader, PEImgContentWrapper, PEIndicator, SelectBoxContainer, SelectBoxWrapper } from './Work';
import { useImageStyleJudge } from '../../shared/hooks/useImageStyleJudge';
import DocumentInfo from './DocumentInfo';
import DocumentInfoLeft from './DocumentInfoLeft';
import DocumentInfoRight from './DocumentInfoRight';
import DocumentImgInPe from './DocumentImgInPe';
import DocumentVideoInPe from './DocumentVideoInPe';


export interface DocumentProps {
  peId: string;
  alignment: DisplayAlignment;
  displaySize: DisplaySize;
  infoType?: PEInfoType;
  data: DocumentData;
  priority: boolean;
}

const Document: React.FC<DocumentProps> = ({ peId, alignment, displaySize, infoType, data, priority }) => {
  const { isEditMode } = useEditMode();
  const handleDisplayAlignmentMobile = useProjectElementListStore((s) => s.updateDisplayAlignment);
  const handleDisplaySizeChange = useProjectElementListStore((s) => s.updateDisplaySize);
  const handleInfoTypeChange = useProjectElementListStore((s) => s.updateInfoType);
  const handleChange = useProjectElementListStore((s) => s.updateDocument);
  const { judgeImageStyle } = useImageStyleJudge();
  const imageStyle = useMemo(() => judgeImageStyle(data.uploadFile?.width / data.uploadFile?.height), [data.uploadFile?.width, data.uploadFile?.height]);
  const effectiveInfoType = normalizePEInfoType(infoType);
  const detailType = data.detailType ?? DetailType.IMAGE;

  return (
    <PEImgContentWrapper id={`project-element-document-${peId}`}>
      {isEditMode &&
        <PEHeader>
          <SelectBoxContainer>
            <SelectBoxWrapper>
              <SelectBox
                value={displaySize || DisplaySize.BIG}
                selectType={SelectType.WORK_SIZE}
                handleChange={(value) => handleDisplaySizeChange(peId, value)}
                direction={false} />
            </SelectBoxWrapper>
            <SelectBoxWrapper>
              <SelectBox
                value={alignment}
                selectType={SelectType.DISPLAY_ALIGNMENT}
                handleChange={(value) => handleDisplayAlignmentMobile(peId, value)}
                direction={false} />
            </SelectBoxWrapper>
            <SelectBoxWrapper>
              <SelectBox
                value={effectiveInfoType}
                selectType={SelectType.PE_INFO_TYPE}
                handleChange={(value) => handleInfoTypeChange(peId, value)}
                direction={false} />
            </SelectBoxWrapper>
          </SelectBoxContainer>
          <PEIndicator peId={peId}>: Visual Aid</PEIndicator>
        </PEHeader>
      }
      <ImgContentCoreWrapper>
        <ImgWrapper>
          <DocumentInfoLeft
            peId={peId}
            alignment={alignment || DisplayAlignment.CENTER}
            displaySize={displaySize || DisplaySize.BIG}
            imageStyle={imageStyle || ImageStyle.LANDSCAPE}
            infoType={effectiveInfoType}
            data={data} />
          {detailType === DetailType.VIDEO ? (
            <DocumentVideoInPe
              peId={peId}
              alignment={alignment || DisplayAlignment.CENTER}
              displaySize={displaySize || DisplaySize.BIG}
              data={data}
              priority={priority}
              imageStyle={imageStyle || ImageStyle.LANDSCAPE}
              onChangeType={() => handleChange(peId, { detailType: DetailType.IMAGE })}
              onVideoUrlChange={(videoUrl) => handleChange(peId, { videoUrl })}
            />
          ) : (
            <DocumentImgInPe
              peId={peId}
              alignment={alignment || DisplayAlignment.CENTER}
              displaySize={displaySize || DisplaySize.BIG}
              data={data}
              priority={priority}
              imageStyle={imageStyle || ImageStyle.LANDSCAPE}
              onChangeType={() => handleChange(peId, { detailType: DetailType.VIDEO })}
            />
          )}
          <DocumentInfoRight
            peId={peId}
            alignment={alignment || DisplayAlignment.CENTER}
            displaySize={displaySize || DisplaySize.BIG}
            imageStyle={imageStyle || ImageStyle.LANDSCAPE}
            infoType={effectiveInfoType}
            data={data} />
        </ImgWrapper>
        <DocumentInfo
          peId={peId}
          alignment={alignment || DisplayAlignment.CENTER}
          displaySize={displaySize || DisplaySize.BIG}
          imageStyle={imageStyle || ImageStyle.LANDSCAPE}
          infoType={effectiveInfoType}
          data={data} />
      </ImgContentCoreWrapper>
    </PEImgContentWrapper >
  );
}


export default Document;
